// ─────────────────────────────────────────────────────────────────────────────
// File: components/weather/WeatherPanel.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from 'react';
import seedWeatherData from '../../data/weatherData.json';
import { normalizeHourly } from './utils';
import { DateSwitcher, DailySummary, HourlyChart /*, HourlyTable*/ } from './index';

export default function WeatherPanel() {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    // mémorise l’état entre les sessions
    const saved = localStorage.getItem('weatherPanel.collapsed');
    return saved === 'true';
  });

  // récupération des données
  console.log('API URL:', process.env.REACT_APP_API_URL);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/data';
  useEffect(() => {
    fetch(API_URL)
      .then((r) => r.json())
      .then((payload) => {
        const incoming = payload?.data ?? payload ?? seedWeatherData ?? null;
        setData(incoming);
        console.log('Données météo reçues :', incoming);
      })
      .catch((err) => {
        console.error('Erreur lors de la récupération des données météo :', err);
        if (seedWeatherData) setData(seedWeatherData);
      });
  }, []);

  // dérivés
  const { byDate, dates } = useMemo(() => {
    const hours = data?.hours || data?.list || data?.data || [];
    const normalized = normalizeHourly(hours);
    const d = Object.keys(normalized.byDate || {}).sort();
    return { byDate: normalized.byDate || {}, dates: d };
  }, [data]);

  // init date sélectionnée
  useEffect(() => {
    if (!date && dates.length > 0) setDate(dates[0]);
  }, [dates, date]);

  // navigation clavier ← →
  useEffect(() => {
    if (!dates.length || !date) return;

    const isTypingField = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const editable = el.getAttribute?.('contenteditable');
      return (
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        editable === '' ||
        editable === 'true'
      );
    };

    const handleKeyDown = (e) => {
      if (isTypingField(document.activeElement)) return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

      const idx = dates.indexOf(date);
      if (idx === -1) return;

      if (e.key === 'ArrowRight') {
        setDate(dates[(idx + 1) % dates.length]);
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        setDate(dates[(idx - 1 + dates.length) % dates.length]);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dates, date]);

  // persister l’état replié
  useEffect(() => {
    localStorage.setItem('weatherPanel.collapsed', String(collapsed));
  }, [collapsed]);

  // états d’attente
  if (!data) return <div>Chargement des données météo...</div>;
  if (!dates.length) return <div>Aucune donnée horaire disponible.</div>;
  if (!date) return <div>Initialisation de l’affichage…</div>;

  const rows = byDate[date] || [];
  const contentId = 'weather-details';

  return (
    <div style={{ padding: 16 }}>
      {/* En-tête avec bouton de repli */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>
          Condition au Chariot {date && `(${date})`}
        </h2>

        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={contentId}
          title={collapsed ? 'Déplier' : 'Replier'}
          style={{
            border: '1px solid #ddd',
            borderRadius: 8,
            padding: '6px 10px',
            background: '#f8f8f8',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          {collapsed ? '▶ Afficher' : '▼ Replier'}
        </button>
      </div>

      <p style={{ marginTop: 0, color: '#666' }}>Mise à jour: {data.lasteUpdate}</p>

      {/* Contenu repliable */}
      <div
        id={contentId}
        style={{
          overflow: 'hidden',
          transition: 'grid-template-rows 180ms ease, opacity 180ms ease',
          display: 'grid',
          gridTemplateRows: collapsed ? '0fr' : '1fr',
          opacity: collapsed ? 0 : 1,
        }}
      >
        <div style={{ minHeight: 0 }}>
          <DateSwitcher dates={dates} value={date} onChange={setDate} />
          <DailySummary rows={rows} />
          <HourlyChart rows={rows} />
          {/* <HourlyTable rows={rows} /> */}
        </div>
      </div>
    </div>
  );
}
